<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import ReadMoreIndowStories from '$lib/PageSections/ReadMoreIndowStories/ReadMoreIndowStories.svelte';

	import caseStudySinglePaneWindowInsulation from './images/caseStudySinglePaneWindowInsulation.jpg';
	import churchIndowInserts from './images/churchIndowInserts.jpg';
	import indowWindowInsertsStainedGlassWindows from './images/indowWindowInsertsStainedGlassWindows.jpg';
	import installingIndowInsertsChurchStainedGlassWindows from './images/installingIndowInsertsChurchStainedGlassWindows.jpg';
	import installingWindowInsertsChurchWindows from './images/installingWindowInsertsChurchWindows.png';
	import mcminnvilleCooperativeMinistries from './images/mcminnvilleCooperativeMinistries.jpg';
	import stainedGlassWindowsAtMcminnvilleCooperativeMinistries from './images/stainedGlassWindowsAtMcminnvilleCooperativeMinistries.png';
</script>

<Section>
	<p>
		Many congregations have a shared problem: church heating. The congregation
		can radiate warmth, but the buildings tend to have an issue with temperature
		control. We can generally blame the large, beautiful windows. But placing
		church storm windows over intricate stained glass? Or, church window
		replacement when they are associated with hundreds of years of history?
		Neither of these are good options.
	</p>
	<p>
		Poor church heating is distracting to the congregation and expensive to
		maintain. Church storm windows can help control some heating and cooling,
		but they cannot accommodate the unique and varied shapes of church windows.
		Indow window inserts solve church heating: they can fit any shape, disappear
		once installed, and block 100% of drafts.
	</p>
	<Image
		alt="exterior of church with indow insert church storm windows"
		src={churchIndowInserts}
	/>
	<p>
		Indow is one of the only solutions that can fit any kind of window shape
		because of our precision measuring and customization. Learn more about how
		we create our special shape window inserts.
	</p>
	<Bubble>
		{#snippet titleSlot()}
			<p class="font-bold">
				The benefits of keeping your windows and insulating with Indow window
				inserts:
			</p>
		{/snippet}
		<ul>
			<li>Help regulate church heating & cooling</li>
			<li>
				Remove the need for church storm windows which can harm historic church
				windows
			</li>
			<li>Solve how to insulate stained glass windows</li>
			<li>Are customizable and fit any shape of window</li>
			<li>Block outside noise so congregants aren’t distracted</li>
		</ul>
	</Bubble>
</Section>

<Section>
	<h2>Single-Pane Historic Windows in Bowdoin College Church</h2>
	<p>
		Bowdoin college church sits in Banister Hall in Brunswick, Maine. The
		Romanesque building was designed by Richard Upjohn and has striking, arched,
		single-pane historic windows. The building can radiate warmth, but the
		windows were letting in a lot of cold weather.
	</p>
	<p>
		At first, church storm windows were added, but they didn’t look right and so
		the college removed them. Replacing the windows was out of the question.
		These single-pane historic windows, though drafty, are a part of what makes
		this building iconic. Church window replacements cannot replicate the
		intricate windows students and congregants look out each day.
	</p>
	<Image
		alt="historic arched windows"
		src={caseStudySinglePaneWindowInsulation}
	/>
</Section>

<Section>
	<h2>Bowdoin College Church Heating Solution</h2>
	<p>
		Bowdoin College wanted a solution that helped solve church heating, saved
		their single-pane historic windows, and was sustainable. The campus
		sustainability coordinator had been working to reduce Bowdoin’s carbon
		footprint through measures such as weatherization, switching from oil to
		natural gas, and installing energy-efficient lighting. She reached out to
		Indow to do a pilot test with a single office on the building’s south side.
	</p>
	<p>
		The <a href="/products/thermal-window-inserts">window inserts</a> created a huge
		impact on the church heating. “They eliminated the drafts,” said Caitlin Callahan,
		assistant director of the McKeen Center for the Common Good, who worked in the
		office at the time. But she noticed something else too: the chapel bells that
		ring every quarter hour were quieter.
	</p>
	<p>
		So the college purchased 19 Indow inserts for the building’s south side.
		Because Indow inserts are precisely measured to fit any window, the inserts
		were custom fit to the stunning arches and batwing windows. The north side
		of Banister Hall houses the American Musicological Society, which is now
		interested in having Indow inserts as well.
	</p>
</Section>

<Section backgroundColor="grey-light">
	<h2>Testimonials</h2>
	<Image
		alt="installing indow inserts in church single pane historic windows"
		src={installingWindowInsertsChurchWindows}
	/>
	<div class="grid justify-items-center gap-y-2">
		<p class="text-lg text-indow-blue! italic">
			“If it’s blocking that much sound, it’s helping with the cold.”
		</p>
		<p>
			– Caitlin Callahan, Assistant Director of the McKeen Center for the Common
			Good
		</p>
	</div>
	<div class="grid justify-items-center gap-y-2">
		<p class="text-lg text-indow-blue! italic">
			“They’re nearly invisible. Unless you know they’re there, you don’t know
			they’re there.”
		</p>
		<p>– Shawn Gerwig, Administrative Coordinator for the McKeen Center</p>
	</div>
	<a href="/resources/case-studies" class="italic">Read more case studies.</a>
</Section>

<Section>
	<h2>
		Insulating Stained Glass Windows in McMinnville Cooperative Ministries
	</h2>
	<p>
		Church heating was also an issue for McMinnville Cooperative church who
		needed help insulating stained glass windows. They decided that carbon
		reduction was an important priority and proposed a plan that included air
		and duct sealing, wall and ceiling insulation, an upgrade to the heating
		system, and installation of solar power. WIth all those upgrades, they still
		had to figure out how to insulate stained glass windows.
	</p>
	<p>
		The church contacted Cellar Ridge who recommended Indow window inserts
		instead of traditional church storm windows. This way, the windows remain
		intact and aren’t blocked by clunky church storm windows.
	</p>
	<Image
		alt="exterior view of church indow window inserts insulating stained glass windows"
		src={mcminnvilleCooperativeMinistries}
	/>
</Section>

<Section>
	<h2>McMinnville Cooperative Ministries Church Heating Solution</h2>
	<p>
		The church’s plan for energy efficiency was actually voted on by two entire
		congregations. When the leaders presented the renovations to all members,
		they weren’t sure if everyone would agree that a smaller carbon footprint
		was that important. It was, and so was saving and insulating the stained
		glass windows. This building had been a sanctuary for the rebirth and
		mending of many of their lives, now it was time to lovingly restore it.
	</p>
	<Image
		alt="closeup of church window inserts insulating stained glass windows"
		src={indowWindowInsertsStainedGlassWindows}
	/>
	<p>
		The sanctuary has beautiful church stained glass windows. Cellar Ridge
		Energy Works recommended church replacement windows for other areas of the
		building, but when it came to the stained glass windows, they brought in
		something that would upgrade their energy efficiency without hiding any of
		their beauty like church storm windows would. Indow inserts insulated all
		the church stained glass windows while letting in beautiful natural light.
	</p>
	<p>
		McMinnville Cooperative Ministries reduce its carbon footprint by 80%. Plus,
		their sanctuary now shelters from the cold and noise. They chose Indow
		window inserts instead of traditional church storm windows and gained an
		easy installation, superior insulation, and remarkable <a
			href="/solutions/window-noise-reduction">window noise reduction</a
		>.
	</p>
</Section>

<Section backgroundColor="grey-light">
	<h2>Testimonials</h2>
	<Image
		alt="installing indow inserts in church stained glass windows"
		src={stainedGlassWindowsAtMcminnvilleCooperativeMinistries}
	/>
	<div class="grid justify-items-center gap-y-2">
		<p class="text-lg text-indow-blue! italic">
			“I had read about Indow in the newspaper just a week before being
			presented the weatherization plan. It seemed like a great way to insulate
			the stained glass windows while preserving their beauty. 68 degrees is so
			much warmer than it used to be. The temperature is the same, but the room
			is much cozier because the cold surface of the stained glass windows no
			longer sucks the heat out of our bodies. When I remove an insert to show a
			congregant how they work, we can feel the cold air rushing in.”
		</p>
		<p>
			– Pastor Pederson, McMinnville Cooperative Ministries, McMinnville Oregon.
		</p>
	</div>
	<a href="/resources/case-studies" class="italic">Read more case studies.</a>
</Section>

<Section>
	<h2>A Glowing Review: Better Than Church Storm Windows</h2>
	<p>
		Both churches, regardless of the size and shape of their windows were able
		to solve their church heating issues. Each one was able to maintain a clear
		view of their windows with natural light flowing through to their
		congregants. Even though the window inserts were brought in for church
		heating, each noted surprise at the fact that as each insert was installed,
		their sanctuary became quieter and quieter, “Once one side of the building
		was installed, the difference was remarkable!” said Pederson.
	</p>
	<p>
		After installing window inserts in their church windows, everyone was glad
		they skipped the church storm windows for something more illuminating.
	</p>
	<Image
		alt="person installing indow window church storm windows"
		src={installingIndowInsertsChurchStainedGlassWindows}
	/>
	<p>
		Hear from others who have used Indow <a
			href="/commercial-storm-windows/testimonials">commercial window inserts</a
		> in their historic buildings. Business owners, building managers, and architects
		have all used window inserts to help those who use the space be more comfortable.
		Or, speak with one of our window experts to get a free estimate for your window
		solution.
	</p>
</Section>

<GetAFreeEstimate />
<ReadMoreIndowStories />
